import { type ShortUrlResponse } from '@air/api/types';

import { type PublicBoardProps } from '~/components/Layouts/PublicBoardLayout/PublicBoard';
import { type PublicCanvasProps } from '~/components/Layouts/PublicCanvasLayout/PublicCanvas';
import { type PublicClipProps } from '~/components/Layouts/PublicClipLayout/PublicClip';
import { QueryParamNames } from '~/constants/search';
import { type PublicShowcaseShareData } from '~/swr-hooks/shortUrl/utils';

/**
 * ISR props for a collection share link (`/a/m<shortId>`) — purely the route's own object, the
 * collection, mirroring how every other route type carries its object under `initialData`
 * (`getShareLinkForcedTheme` relies on `initialData.presentationSettings` living at the same path on
 * all of them). Deep-linked descendants (`/b/{boardId}`, `/c/{clipId}`) contribute NO SSR payload:
 * the collection tree always mounts, and the drilled board/clip fetch their own data inside it
 * (see PublicShowcaseBoardView).
 */
export type PublicShowcaseRouteProps = {
  initialData: PublicShowcaseShareData;
  shortId: string;
};

export type PasscodeRequiredProps = {
  shortId: string;
  passcodeRequired: true;
  boardId: string | null;
  clipId: string | null;
};

export type NotFoundProps = {
  shortId: string | undefined;
  notFound: true;
  passcodeRequired?: false;
};

export type ShortUrlPageProps =
  | PublicBoardProps
  | PublicCanvasProps
  | PublicClipProps
  | PublicShowcaseRouteProps
  | PasscodeRequiredProps
  | NotFoundProps;

// feature checks
export const isPasscodeProtected = (props: ShortUrlPageProps): props is PasscodeRequiredProps =>
  'passcodeRequired' in props && props.passcodeRequired === true;

/**
 * The route helpers below classify by shortId PREFIX, so a NotFound payload for a valid-looking
 * shortId (deleted link, upstream error during ISR) still satisfies them while carrying no
 * `initialData`. Check this FIRST — it is the only honest discriminator for that case.
 */
export const isNotFoundRoute = (props: ShortUrlPageProps): props is NotFoundProps =>
  'notFound' in props && props.notFound === true;

// route helpers
export const isBoardRoute = (props: ShortUrlPageProps): props is PublicBoardProps => !!props.shortId?.startsWith('b');

export const isAssetOrClipRoute = (props: ShortUrlPageProps): props is PublicClipProps =>
  !!props.shortId?.startsWith('c') || !!props.shortId?.startsWith('a');

export const isCanvasRoute = (props: ShortUrlPageProps): props is PublicCanvasProps => !!props.shortId?.startsWith('v');

export const isShowcaseRoute = (props: ShortUrlPageProps): props is PublicShowcaseRouteProps =>
  !!props.shortId?.startsWith('m');

export const getFullShareUrl = (id: ShortUrlResponse['id'], searchParams?: URLSearchParams) =>
  `${window.location.origin}/a/${id}${searchParams ? `?${searchParams.toString()}` : ''}`;

export const getCanvasPreviewSearchParams = (previewNodeId: string | undefined) =>
  previewNodeId ? new URLSearchParams({ [QueryParamNames.preview]: previewNodeId }) : undefined;
